import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText } from "lucide-react";
import { format } from "date-fns";
import { generateTermsTemplate, type TermsTemplateData } from "@/lib/termsTemplate";
import { type TermsVariables } from "@/hooks/useContest";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";


interface TermsModalProps {
  termsVariables: TermsVariables;
  referralBonusEntries: number;
  endsAt?: string | null;
  startsAt?: string | null;
  children: React.ReactNode;
  useCustomTerms?: boolean;
  customTermsText?: string | null;
}

export { type TermsVariables };

export function TermsModal({
  termsVariables,
  referralBonusEntries,
  endsAt,
  startsAt,
  children,
  useCustomTerms,
  customTermsText,
}: TermsModalProps) {
  // Compute derived dates
  const endDateFormatted = endsAt ? format(new Date(endsAt), "MMMM d, yyyy") : "N/A";
  const endTimeFormatted = endsAt ? format(new Date(endsAt), "h:mm a z") : "N/A";
  const startDateFormatted = startsAt
    ? format(new Date(startsAt), "MMMM d, yyyy")
    : termsVariables.startDate || "N/A";
  const startTimeFormatted = startsAt
    ? format(new Date(startsAt), "h:mm a z")
    : termsVariables.startTime || "12:00 AM";

  // Map to TermsTemplateData
  const templateData: TermsTemplateData = {
    sponsorName: termsVariables.sponsorName || "[Sponsor Name]",
    sponsorAddress: termsVariables.sponsorAddress || "[Sponsor Address]",
    supportEmail: termsVariables.supportEmail || "",
    eligibleRegions: termsVariables.eligibleRegions || "[Eligible Regions]",
    minimumAge: termsVariables.minimumAge || "18",
    excludedStates: termsVariables.excludedStates,
    startDate: startDateFormatted,
    startTime: startTimeFormatted,
    endDate: endDateFormatted,
    endTime: endTimeFormatted,
    entryMethods: "Complete the actions listed on the contest page.", // Dynamic based on contest logic? For now, standard text.
    mailInAddress: termsVariables.mailInAddress,
    entryLimit: termsVariables.entryLimit,
    prizeDescription: termsVariables.prizeDescription || "[Prize Description]",
    prizeArv: termsVariables.prizeArv || "[Prize ARV]",
    prizeQuantity: termsVariables.prizeQuantity || "One (1)",
    totalPrizeValue: termsVariables.totalPrizeValue,
    winnerNotificationMethod: termsVariables.winnerNotificationMethod || "email",
    winnerNotificationTimeframe: termsVariables.winnerNotificationTimeframe || "7 days",
    responseWindow: termsVariables.responseWindow || "48 hours",
    winnerSelectionDate: termsVariables.winnerSelectionDate,
    referralBonusEntries: referralBonusEntries,
    governingState: termsVariables.governingState || "[State]",
    includeMarketingOptIn: termsVariables.includeMarketingOptIn === "yes",
  };

  const isCustomMode = useCustomTerms && customTermsText && customTermsText.trim().length > 0;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Competition Terms & Conditions
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="prose prose-sm max-w-none text-foreground prose-headings:font-bold prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground">
            {isCustomMode ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {customTermsText!}
              </ReactMarkdown>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {generateTermsTemplate(templateData)}
              </ReactMarkdown>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}